<template>
  <el-dialog
    :title="$t('收料确认')"
    :visible.sync="dialogVisible"
    width="960px"
    top="6vh"
    append-to-body
    :close-on-click-modal="false"
    @close="handleClose"
  >
    <div v-loading="loading" element-loading-text="加载中...">
      <el-form
        :model="form"
        label-width="88px"
        size="small"
        class="receive-form"
      >
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item :label="$t('收料日期')" required>
              <el-date-picker
                v-model="form.baseReceiveDate"
                type="datetime"
                value-format="yyyy-MM-dd HH:mm:ss"
                format="yyyy-MM-dd HH:mm:ss"
                :placeholder="$t('请选择收料日期')"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('收料人')" required>
              <el-input
                v-model="form.baseReceiverName"
                :placeholder="$t('请输入收料人')"
                clearable
                disabled
              />
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>

      <el-table
        :data="form.detailList"
        border
        size="small"
        max-height="420"
      >
        <el-table-column
          :label="$t('序号')"
          type="index"
          width="60"
        />
        <el-table-column
          :label="$t('物料编号')"
          prop="baseMaterialNumber"
          min-width="110"
          show-overflow-tooltip
        />
        <el-table-column
          :label="$t('物料名称')"
          prop="baseMaterialName"
          min-width="120"
          show-overflow-tooltip
        />
        <el-table-column
          :label="$t('申请数量')"
          prop="baseRequisitionQuantity"
          width="90"
          align="center"
        />
        <el-table-column
          :label="$t('可收料数量')"
          prop="valueCanReceiveQuantity"
          width="100"
          align="center"
        />
        <el-table-column :label="$t('收料数量')" width="120" align="center">
          <template slot-scope="scope">
            <el-input-number
              v-model="scope.row.valueActualReceiveQuantity"
              :min="0"
              :controls="false"
              size="mini"
              style="width: 100%"
            />
          </template>
        </el-table-column>
        <el-table-column :label="$t('批次号')" width="130">
          <template slot-scope="scope">
            <el-input
              v-model="scope.row.valueBatchNumber"
              size="mini"
              :placeholder="$t('批次号')"
              clearable
            />
          </template>
        </el-table-column>
        <el-table-column :label="$t('状态')" width="120">
          <template slot-scope="scope">
            <el-select
              v-model="scope.row.valueExceptionType"
              size="mini"
              style="width: 100%"
              popper-append-to-body
              @change="handleExceptionTypeChange(scope.row)"
            >
              <el-option
                v-for="item in exceptionTypeOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
          </template>
        </el-table-column>
        <el-table-column :label="$t('异常说明')" min-width="160">
          <template slot-scope="scope">
            <el-input
              v-model="scope.row.valueExceptionRemark"
              type="text"
              size="mini"
              :disabled="isNormalRow(scope.row)"
              :placeholder="isNormalRow(scope.row) ? $t('正常无需填写') : $t('请填写异常说明')"
            />
          </template>
        </el-table-column>
        <el-table-column :label="$t('异常附件')" width="120" align="center">
          <template slot-scope="scope">
            <el-button
              v-if="!isNormalRow(scope.row)"
              size="mini"
              type="primary"
              @click="openExceptionFileDialog(scope.row, scope.$index)"
            >
              {{ getExceptionFileLabel(scope.row) }}
            </el-button>
            <span v-else class="text-muted">—</span>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <el-dialog
      :title="$t('异常附件')"
      :visible.sync="uploadDialogVisible"
      width="720px"
      append-to-body
      :close-on-click-modal="false"
      @close="closeExceptionFileDialog"
    >
      <myUploadTable
        :list="uploadTableList"
        :type="3"
        :status="0"
        :mark-index="uploadRowIndex"
        @uploadSuccess="handleExceptionUploadSuccess"
        @uploadDelete="handleExceptionUploadDelete"
      />
    </el-dialog>

    <div slot="footer" class="dialog-footer">
      <el-button size="small" @click="handleClose">{{ $t('取消') }}</el-button>
      <el-button
        size="small"
        type="primary"
        :loading="submitting"
        @click="handleSubmit"
      >{{ $t('确认收料') }}</el-button>
    </div>
  </el-dialog>
</template>

<script>
import {
  outsorRequisitionDoc_view,
  outsorRequisitionDoc_confirmReceive,
  processOutsourcingRequisitionDoc_view,
  processOutsourcingRequisitionDoc_confirmReceive,
} from '@/api/myController/outsourcing'
import {
  RECEIVE_EXCEPTION_TYPE_OPTIONS,
  buildConfirmReceivePayload,
  getSupplierConfirmStatus,
  initReceiveForm,
  isNormalReceiveException,
  validateReceiveForm,
} from '@/utils/outsourcingReceiveConfirm'

export default {
  name: 'ReceiveConfirmDialog',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    requisitionDocId: {
      default: null,
    },
    sourceDetailList: {
      type: Array,
      default: () => [],
    },
    sourceDoc: {
      type: Object,
      default: () => ({}),
    },
    /** product | process */
    bizType: {
      type: String,
      default: 'product',
    },
  },
  data() {
    return {
      loading: false,
      submitting: false,
      form: initReceiveForm(),
      uploadDialogVisible: false,
      uploadTableList: [],
      currentUploadRow: null,
      uploadRowIndex: 0,
    }
  },
  computed: {
    isProcessBiz() {
      return this.bizType === 'process'
    },
    exceptionTypeOptions() {
      return RECEIVE_EXCEPTION_TYPE_OPTIONS
    },
    dialogVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
      },
    },
  },
  watch: {
    visible(val) {
      if (val) {
        this.openDialog()
      }
    },
  },
  methods: {
    $t(value) {
      return value
    },
    isNormalRow(row) {
      return isNormalReceiveException(row.valueExceptionType)
    },
    handleExceptionTypeChange(row) {
      if (this.isNormalRow(row)) {
        row.valueExceptionRemark = ''
        row._exceptionFiles = []
      }
    },
    openDialog() {
      if (this.sourceDetailList && this.sourceDetailList.length) {
        this.initForm(
          JSON.parse(JSON.stringify(this.sourceDetailList)),
          this.sourceDoc
        )
        return
      }
      const docId = this.requisitionDocId
      if (!docId) {
        this.$message.warning(this.$t('缺少发料单标识，无法收料'))
        this.handleClose()
        return
      }
      this.loading = true
      const viewApi = this.isProcessBiz
        ? processOutsourcingRequisitionDoc_view
        : outsorRequisitionDoc_view
      const viewParams = this.isProcessBiz
        ? { processOutsourcingRequisitionDocId: docId }
        : { outsourcingRequisitionDocId: docId }
      viewApi(viewParams)
        .then((res) => {
          const data = res.data.data || {}
          const detailList =
            data.outsorRequisitionDocDetailList ||
            data.processOutsourcingRequisitionDocDetailList ||
            data.processOutsourcingRequisitionDocDetails ||
            []
          if (!detailList.length) {
            this.$message.warning(this.$t('暂无发料明细，无法收料'))
            this.handleClose()
            return
          }
          this.initForm(detailList, data)
        })
        .catch(() => {
          this.handleClose()
        })
        .finally(() => {
          this.loading = false
        })
    },
    initForm(detailList, doc = {}) {
      const docStatus = getSupplierConfirmStatus(doc)
      const enrichedList = (detailList || []).map((item) => ({
        ...item,
        statusSupplierConfirm:
          getSupplierConfirmStatus(item) ?? docStatus,
      }))
      this.form = initReceiveForm({
        detailList: enrichedList,
        baseReceiveDate: this.$moment().format('YYYY-MM-DD HH:mm:ss'),
        baseReceiverName: this.getUserInfo?.showName || '',
      })
    },
    getExceptionFileCount(row) {
      return Array.isArray(row._exceptionFiles) ? row._exceptionFiles.length : 0
    },
    getExceptionFileLabel(row) {
      const count = this.getExceptionFileCount(row)
      return count > 0 ? `${this.$t('附件')}(${count})` : this.$t('上传附件')
    },
    openExceptionFileDialog(row, index) {
      this.currentUploadRow = row
      this.uploadRowIndex = index
      if (!Array.isArray(row._exceptionFiles)) {
        row._exceptionFiles = []
      }
      this.uploadTableList = row._exceptionFiles
      this.uploadDialogVisible = true
    },
    closeExceptionFileDialog() {
      this.uploadDialogVisible = false
      this.uploadTableList = []
      this.currentUploadRow = null
      this.uploadRowIndex = 0
    },
    syncExceptionFiles(list) {
      if (!this.currentUploadRow) return
      this.currentUploadRow._exceptionFiles = list
      this.uploadTableList = list
    },
    handleExceptionUploadSuccess(list) {
      this.syncExceptionFiles(list)
    },
    handleExceptionUploadDelete(list) {
      this.syncExceptionFiles(list)
    },
    handleSubmit() {
      if (!this.requisitionDocId) {
        this.$message.warning(this.$t('缺少发料单标识，无法收料'))
        return
      }
      const validateMsg = validateReceiveForm(this.form)
      if (validateMsg) {
        this.$message.warning(validateMsg)
        return
      }
      const payload = buildConfirmReceivePayload({
        requisitionDocId: this.requisitionDocId,
        detailList: this.form.detailList,
        baseReceiveDate: this.form.baseReceiveDate,
        baseReceiverName: this.form.baseReceiverName,
      })
      this.submitting = true
      const confirmApi = this.isProcessBiz
        ? processOutsourcingRequisitionDoc_confirmReceive
        : outsorRequisitionDoc_confirmReceive
      confirmApi(payload)
        .then(() => {
          this.$message.success(this.$t('收料成功'))
          this.$emit('success')
          this.handleClose()
        })
        .finally(() => {
          this.submitting = false
        })
    },
    handleClose() {
      this.closeExceptionFileDialog()
      this.dialogVisible = false
      this.form = initReceiveForm()
    },
  },
}
</script>

<style scoped>
.receive-form {
  margin-bottom: 12px;
}

.text-muted {
  color: #c0c4cc;
}
</style>
